import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { assistantApiUrl, assistantRequestInit } from '@/lib/assistant-request';

import type { MemoriesListResponse } from './use-memories';

interface EditMemoryInput {
  id: string;
  content: string;
  /**
   * `YYYY-MM-DD` to set an expiry, `null` to clear it, omitted to leave the
   * existing one alone. Sent as a calendar date so the SERVER resolves it
   * against the org timezone.
   */
  expiresAt?: string | null;
  /**
   * Skill id(s) this memory applies to (Phase 3,
   * docs/plans/claire-memory-suggestions.md). `null`/`[]` to clear it back
   * to "applies everywhere", omitted to leave the existing scope alone.
   */
  scopeSkillIds?: string[] | null;
  /** Opt-out-without-delete. Omitted leaves the existing value alone. */
  enabled?: boolean;
}

interface EditMemoryResponse {
  knowledgeEntryId: string;
  content: string;
  expiresAt: string | null;
  scopeSkillIds: string[] | null;
  enabled: boolean;
  updatedAt: string;
}

interface UseEditMemoryOptions {
  onSuccess?: (memory: EditMemoryResponse) => void;
}

/**
 * Mutation hook for `PATCH /assistant/memories/:id`.
 *
 * On success: invalidates the memories list cache so the row refreshes
 * with the new content + updatedAt. Optimistic in-place update on
 * `onMutate` keeps the UI responsive; rolls back on error.
 *
 * Hard-block 400s come back with a human-readable message — show the
 * server's text verbatim (per W-C14-backend handoff note 2).
 */
export const useEditMemory = (options?: UseEditMemoryOptions) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      id,
      content,
      expiresAt,
      scopeSkillIds,
      enabled,
    }: EditMemoryInput) => {
      const res = await fetch(
        assistantApiUrl(`memories/${id}`),
        assistantRequestInit({
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          // Each optional field is a genuinely different edit when OMITTED
          // vs. sent as `null`/a value — the server tells those apart by
          // whether the KEY is present at all, so an omitted field must
          // never be serialised as `undefined`.
          body: JSON.stringify({
            content,
            ...(expiresAt === undefined ? {} : { expiresAt }),
            ...(scopeSkillIds === undefined ? {} : { scopeSkillIds }),
            ...(enabled === undefined ? {} : { enabled }),
          }),
        })
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message =
          (data && typeof data === 'object' && 'message' in data
            ? (data as { message?: string }).message
            : null) ??
          (data && typeof data === 'object' && 'error' in data
            ? (data as { error?: string }).error
            : null) ??
          'Failed to update memory';
        throw new Error(message);
      }
      return data as EditMemoryResponse;
    },
    onMutate: async ({ id, content, expiresAt, scopeSkillIds, enabled }) => {
      await queryClient.cancelQueries({
        queryKey: ['assistant', 'memories'],
      });

      const snapshots = queryClient.getQueriesData<MemoriesListResponse>({
        queryKey: ['assistant', 'memories'],
      });

      for (const [key, value] of snapshots) {
        if (!value) continue;
        queryClient.setQueryData<MemoriesListResponse>(key, {
          ...value,
          items: value.items.map((m) =>
            m.id === id
              ? {
                  ...m,
                  content,
                  updatedAt: new Date().toISOString(),
                  // The server resolves the date to end-of-day in the org
                  // zone; this optimistic value is only ever on screen for
                  // the moment before the refetch replaces it.
                  ...(expiresAt === undefined
                    ? {}
                    : {
                        expiresAt: expiresAt
                          ? new Date(`${expiresAt}T23:59:59`).toISOString()
                          : null,
                      }),
                  ...(scopeSkillIds === undefined
                    ? {}
                    : {
                        scopeSkillIds:
                          scopeSkillIds && scopeSkillIds.length > 0
                            ? scopeSkillIds
                            : null,
                      }),
                  ...(enabled === undefined ? {} : { enabled }),
                }
              : m
          ),
        });
      }

      return { snapshots };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const [key, value] of ctx.snapshots) {
          queryClient.setQueryData(key, value);
        }
      }
      const message =
        err instanceof Error ? err.message : 'Failed to update memory';
      toast.error(message);
    },
    onSuccess: (memory) => {
      toast.success('Memory updated');
      options?.onSuccess?.(memory);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['assistant', 'memories'],
      });
    },
  });

  return {
    editMemory: mutation.mutate,
    editMemoryAsync: mutation.mutateAsync,
    isEditing: mutation.isPending,
  };
};
